import { Direction, Speed } from '@shared/types.js';
import type { Grid, Vec2 } from '@shared/types.js';
export { Speed };

export const DIR: Record<Direction, Vec2> = {
  [Direction.UP]: { x: 0, y: -1 },
  [Direction.DOWN]: { x: 0, y: 1 },
  [Direction.LEFT]: { x: -1, y: 0 },
  [Direction.RIGHT]: { x: 1, y: 0 },
};

export const OPPOSITE: Record<Direction, Direction> = {
  [Direction.UP]: Direction.DOWN,
  [Direction.DOWN]: Direction.UP,
  [Direction.LEFT]: Direction.RIGHT,
  [Direction.RIGHT]: Direction.LEFT,
};

/** Max pending turns buffered per player so rapid sequences aren't eaten (#61). */
export const MAX_DIR_QUEUE = 2;

/**
 * Buffer a requested turn onto a per-player queue (depth MAX_DIR_QUEUE), drained
 * one turn per tick. Validates against the direction that will be in effect once
 * the queue drains — the last queued turn, or `currentDir` when empty — so a
 * quick perpendicular double-turn (e.g. LEFT then DOWN) both register in order
 * instead of the single-slot model dropping the second. Drops 180° reversals and
 * no-op repeats. Mutates and returns the queue. Shared so server and local play
 * can't drift. Returns true if the turn was queued.
 */
export function enqueueTurn(queue: Direction[], currentDir: Direction, dir: Direction): boolean {
  const ref = queue.length > 0 ? queue[queue.length - 1] : currentDir;
  if (dir === ref || OPPOSITE[dir] === ref) return false;
  if (queue.length >= MAX_DIR_QUEUE) return false;
  queue.push(dir);
  return true;
}

export const PALETTE = [
  { color: '#0ff', trail: '#066' },
  { color: '#f80', trail: '#630' },
  { color: '#f0f', trail: '#606' },
  { color: '#0f0', trail: '#060' },
  { color: '#ff0', trail: '#660' },
  { color: '#f44', trail: '#600' },
] as const;

export const CELL_SIZE = 16;
export const TICK_MS = 75;
export const CRASH_ANIM_TICKS = 12;
// esbuild define: only replaced in client bundle when COUNTDOWN_STEP_MS env is set (e.g. test:e2e)
declare const __COUNTDOWN_STEP_MS__: number | undefined;
export const COUNTDOWN_STEP_MS = typeof __COUNTDOWN_STEP_MS__ !== 'undefined' ? __COUNTDOWN_STEP_MS__ : 600;
export const COUNTDOWN_TOTAL_MS = COUNTDOWN_STEP_MS * 4;

export const SPEED_TICK_MS: Record<Speed, number> = {
  [Speed.Slowest]: 250,
  [Speed.Slow]: 200,
  [Speed.Normal]: 150,
  [Speed.Fast]: 100,
  [Speed.Fastest]: 75,
  [Speed.Turbo]: 50,
  [Speed.Ultra]: 25,
};

export const SPEED_LABEL: Record<Speed, string> = {
  [Speed.Slowest]: 'CRUISE',
  [Speed.Slow]: 'GLIDE',
  [Speed.Normal]: 'DRIVE',
  [Speed.Fast]: 'BOOST',
  [Speed.Fastest]: 'WARP',
  [Speed.Turbo]: 'TURBO',
  [Speed.Ultra]: 'ULTRA',
};

export const SPEED_COLOR: Record<Speed, string> = {
  [Speed.Slowest]: '#0af',
  [Speed.Slow]: '#0ff',
  [Speed.Normal]: '#0f0',
  [Speed.Fast]: '#ff0',
  [Speed.Fastest]: '#f80',
  [Speed.Turbo]: '#f40',
  [Speed.Ultra]: '#f00',
};

export const SPEED_ORDER: Speed[] = [Speed.Slowest, Speed.Slow, Speed.Normal, Speed.Fast, Speed.Fastest];

/** Speed ramp chain for ending bot-only games, from slowest to fastest. */
export const SPEED_RAMP_CHAIN: Speed[] = [Speed.Slowest, Speed.Slow, Speed.Normal, Speed.Fast, Speed.Fastest, Speed.Turbo, Speed.Ultra];
export const SPEED_RAMP_INTERVAL_TICKS = 10;
export const FIREBALL_ORBIT_RADIUS = 1.8;
export const FIREBALL_ORBIT_STEP = 0.3;
export const FIREBALL_COUNT = 9;
export const JUMP_AIRBORNE_TICKS = 1;
export const MAX_PLAYERS = 6;
export const MAX_SPECTATORS_DISPLAY = 6;
export const RECONNECT_GRACE_MS = 15_000;
export const ONLINE_COLS = 24;
export const ONLINE_ROWS = 40;
export const MAX_WS_PAYLOAD = 1024;
export const MAX_NAME_LENGTH = 16;
export const NAME_PATTERN = /^[\w\s\-]+$/;
export const SPAWN_MARGIN_MIN = 0.03;
export const SPAWN_MARGIN_MAX = 0.15;
export const RATE_LIMIT_MAX = 30;
export const RATE_LIMIT_WINDOW_MS = 1000;
export const STORAGE_KEY_NAME = 'tron_player_name';
export const STORAGE_KEY_AUTO_START = 'tron_auto_start';
export const STORAGE_KEY_SOLO_STREAK = 'tron_solo_streak';
export const STORAGE_KEY_SOLO_WINS = 'tron_solo_wins';
export const STORAGE_KEY_UNLOCKS = 'tron_unlocks';
/**
 * Host-selectable auto-start countdown durations. 0 = Auto (context-aware):
 * short when everyone was active last game, a touch longer when the lobby
 * composition changed and players need a moment to orient (#23/#47).
 */
export const AUTO_START_OPTIONS: readonly number[] = [0, 2, 3, 5];
export const AUTO_START_SECONDS_DEFAULT = 0; // Auto
export const AUTO_START_ENGAGED_SECONDS = 2; // previous game fully active
export const AUTO_START_CHANGED_SECONDS = 3; // composition changed (AFK/disconnect/seat turnover)
export const GAMEOVER_LOBBY_DELAY_MS = 2000;
/**
 * Consecutive idle games before a non-host player is demoted to spectator.
 * Host-configurable; 0 (Off) disables AFK demotion entirely.
 */
export const AFK_THRESHOLD_DEFAULT = 2;
export const AFK_THRESHOLD_OPTIONS: readonly number[] = [0, 2, 3, 5];
/** How long the "game in progress" overlay stays up when joining mid-game as a spectator. */
export const SPECTATOR_JOIN_NOTICE_MS = 2500;
/** One full compress cycle of the "YOU" indicator square. */
export const YOU_INDICATOR_COMPRESS_MS = 900;
/** Total compress cycles the "YOU" indicator runs from countdown start, then it's gone. */
export const YOU_INDICATOR_CYCLES = 3;

export const UI_COLOR = {
  PRIMARY: '#0ff',
  READY: '#0f0',
  MUTED: '#666',
  NEUTRAL: '#fff',
  FALLBACK: '#888',
  WARNING: '#ff0',
} as const;

export const SECURITY_HEADERS: Record<string, string> = {
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'Content-Security-Policy': "default-src 'self'; style-src 'unsafe-inline'; img-src 'self' data:; connect-src 'self' wss: ws:",
};

const BOT_PREFIXES = [
  'Byte', 'Pixel', 'Glitch', 'Neon', 'Flux', 'Blip', 'Turbo', 'Zero',
  'Hexa', 'Nano', 'Volt', 'Zinc', 'Axiom', 'Drift', 'Echo', 'Fuse',
  'Grid', 'Jolt', 'Kilo', 'Loop', 'Mux', 'Node', 'Ohm', 'Pulse',
  'Qubit', 'Rex', 'Sync', 'Tix', 'Ux', 'Vex', 'Warp', 'Xor', 'Zap',
];

const BOT_SUFFIXES = [
  '3000', 'X', 'Prime', 'Bot', 'Tron', 'Nova', 'Max', 'Jr',
  'V2', 'Zero', 'One', 'Alpha', 'Beta', 'Omega', 'Lite', 'Pro',
];

export function randomBotName(): string {
  const pre = BOT_PREFIXES[Math.floor(Math.random() * BOT_PREFIXES.length)];
  const suf = BOT_SUFFIXES[Math.floor(Math.random() * BOT_SUFFIXES.length)];
  return pre + suf;
}

export const DIRECTIONS: Direction[] = [Direction.UP, Direction.DOWN, Direction.LEFT, Direction.RIGHT];
export const DIR_VECS: Vec2[] = Object.values(DIR);

export function createGrid(cols: number, rows: number): Grid {
  const grid: Grid = [];
  for (let y = 0; y < rows; y++) grid[y] = new Array(cols).fill(0);
  return grid;
}

export function getSpawnPositions(cols: number, rows: number, slots: number, playerCount?: number) {
  const cx = cols / 2, cy = rows / 2;
  const n = playerCount ?? slots;
  const t = Math.max(0, Math.min(1, (n - 2) / (MAX_PLAYERS - 2)));
  const margin = SPAWN_MARGIN_MAX + t * (SPAWN_MARGIN_MIN - SPAWN_MARGIN_MAX);
  const rx = cols * (0.5 - margin), ry = rows * (0.5 - margin);
  const minX = 1, maxX = cols - 2, minY = 1, maxY = rows - 2;
  const positions: Array<{ x: number; y: number; dir: Direction }> = [];
  for (let i = 0; i < slots; i++) {
    const angle = (2 * Math.PI * i) / slots - Math.PI / 2;
    const x = Math.max(minX, Math.min(maxX, Math.floor(cx + rx * Math.cos(angle))));
    const y = Math.max(minY, Math.min(maxY, Math.floor(cy + ry * Math.sin(angle))));
    const dx = cx - x, dy = cy - y;
    const dir: Direction = Math.abs(dx) > Math.abs(dy)
      ? (dx > 0 ? Direction.RIGHT : Direction.LEFT)
      : (dy > 0 ? Direction.DOWN : Direction.UP);
    positions.push({ x, y, dir });
  }
  return positions;
}
